import { useDialogs } from "../ui/DialogProvider";
import React from "react";
import {
  IMPORT_POLL_INTERVAL,
  IMPORT_PROGRESS_FLAG,
  clearSearchCache,
} from "../utils";
import {
  acknowledgeCollectionImport,
  isCollectionImportActive,
  isUnacknowledgedImport,
  requestCollectionImport,
  resolveCollectionImportResponse,
} from "../utils/collectionImport";
import { writeStoredValue, removeStoredValue } from "../utils/storage";
import { describeApiError } from "../api/request";
export default function useCollectionImport(username, idle) {
  const dialogs = useDialogs();
  const [imported, setImported] = React.useState(false);
  const [importStatus, setImportStatus] = React.useState(null);
  const [startingImport, setStartingImport] = React.useState(false);
  const [importMessage, setImportMessage] = React.useState("");
  const [importResult, setImportResult] = React.useState(null);
  const [dismissal, setDismissal] = React.useState(null);
  const [collectionRevision, setCollectionRevision] = React.useState(0);
  const session = React.useRef(null);
  const pollRef = React.useRef(null);
  const idleRef = React.useRef(idle);
  idleRef.current = idle;
  const refreshCollection = React.useCallback((state, force = false) => {
    if (force || Date.now() - state.lastRefresh >= 15000) {
      state.lastRefresh = Date.now();
      clearSearchCache();
      setCollectionRevision((value) => value + 1);
    }
  }, []);
  const apply = React.useCallback(
    (response, state) => {
      if (session.current !== state) return false;
      state.needsStatus = false;
      // A delayed GET must never restore a job's unacknowledged result.
      const cached = state.acknowledgedJobs.get(response.data.job_id);
      const result = resolveCollectionImportResponse(
        cached
          ? {
              ...response,
              data: cached,
            }
          : response,
      );
      // Stop responses can omit counts; retain the last display snapshot for this job.
      if (
        result.kind === "stopping" &&
        (!result.status.job_id || result.status.job_id === state.job?.job_id)
      ) {
        result.status = { ...state.job, ...result.status };
      }
      state.job = result.status;
      setImportMessage("");
      setImportResult(null);
      if (result.kind === "running" || result.kind === "stopping") {
        setImportStatus(result.status);
        writeStoredValue(IMPORT_PROGRESS_FLAG, state.username);
        refreshCollection(state);
        return true;
      }
      setImportStatus(null);
      clearTimeout(state.timer);
      removeStoredValue(IMPORT_PROGRESS_FLAG);
      if (["completed", "failed", "stopped"].includes(result.kind)) {
        refreshCollection(state, true);
        setImportResult(result.status);
        if (result.status.acknowledged_at !== null)
          state.acknowledgedJobs.set(result.status.job_id, result.status);
      }
      return false;
    },
    [refreshCollection],
  );
  const schedule = React.useCallback((state) => {
    clearTimeout(state.timer);
    if (session.current !== state || document.hidden || idleRef.current) return;
    state.timer = setTimeout(
      () => pollRef.current?.(state),
      Math.min(30000, IMPORT_POLL_INTERVAL * 2 ** state.failures),
    );
  }, []);
  const poll = React.useCallback(
    async (state) => {
      if (
        session.current !== state ||
        document.hidden ||
        idleRef.current ||
        state.polling ||
        state.mutating
      )
        return;
      state.polling = true;
      const request = ++state.version;
      state.pollController = new AbortController();
      try {
        const response = await requestCollectionImport(state.username, {
          signal: state.pollController.signal,
        });
        if (session.current !== state || request !== state.version) return;
        state.failures = 0;
        state.needsStatus = false;
        setImportMessage("");
        if (apply(response, state)) schedule(state);
      } catch (error) {
        if (
          session.current !== state ||
          request !== state.version ||
          error.name === "AbortError"
        )
          return;
        setImportMessage(
          `Status unavailable: ${error.message}. Refreshing status will not restart the import.`,
        );
        if (!error.status || error.status < 400 || error.status >= 500) {
          state.needsStatus = true;
          state.failures += 1;
          schedule(state);
        } else {
          state.needsStatus = false;
          clearTimeout(state.timer);
        }
      } finally {
        if (request === state.version) state.polling = false;
      }
    },
    [apply, schedule],
  );
  pollRef.current = poll;
  React.useEffect(() => {
    const state = {
      username,
      controller: new AbortController(),
      failures: 0,
      lastRefresh: 0,
      version: 0,
      needsStatus: true,
      acknowledgedJobs: new Map(),
      pendingAcknowledgements: new Set(),
    };
    session.current = state;
    setImportStatus(null);
    setStartingImport(false);
    setImportMessage("");
    setImportResult(null);
    setDismissal(null);
    // Authenticated collection views can load immediately, including during imports.
    setImported(!!username);
    if (username) pollRef.current(state);
    const resume = () => {
      if (username && !document.hidden) pollRef.current(state);
    };
    document.addEventListener("visibilitychange", resume);
    return () => {
      session.current = null;
      clearTimeout(state.timer);
      state.controller.abort();
      state.pollController?.abort();
      document.removeEventListener("visibilitychange", resume);
    };
  }, [username]);
  React.useEffect(() => {
    const state = session.current;
    if (!state?.username) return;
    if (idle) clearTimeout(state.timer);
    else if (state.needsStatus || isCollectionImportActive(importStatus))
      schedule(state);
  }, [idle, importStatus, schedule]);
  const startImport = React.useCallback(
    async (user = username) => {
      const state = session.current;
      if (
        !state ||
        !user ||
        user !== username ||
        state?.mutating ||
        isCollectionImportActive(importStatus)
      )
        return;
      state.mutating = true;
      setStartingImport(true);
      state.version += 1;
      state.pollController?.abort();
      state.polling = false;
      setImportMessage("");
      setImportResult(null);
      state.job = null;
      clearTimeout(state.timer);
      try {
        const response = await requestCollectionImport(username, {
          method: "POST",
          signal: state.controller.signal,
        });
        if (apply(response, state)) schedule(state);
      } catch (error) {
        if (session.current === state && error.name !== "AbortError") {
          setImportMessage(
            `Could not start import: ${error.message}. Check status before retrying.`,
          );
          state.needsStatus = true;
          schedule(state);
        }
      } finally {
        state.mutating = false;
        if (session.current === state) setStartingImport(false);
      }
    },
    [username, importStatus, apply, schedule],
  );
  const cancelImport = React.useCallback(async () => {
    const state = session.current;
    if (
      !username ||
      state?.mutating ||
      !isCollectionImportActive(importStatus) ||
      importStatus.status === "stopping"
    )
      return;
    const jobId = state.job?.job_id;
    if (
      !(await dialogs.confirm(
        "Stop this collection import? Already queued enrichment may continue.",
      ))
    )
      return;
    if (
      session.current !== state ||
      state.mutating ||
      state.job?.job_id !== jobId ||
      !isCollectionImportActive(state.job) ||
      state.job.status === "stopping"
    )
      return;
    state.mutating = true;
    state.version += 1;
    state.pollController?.abort();
    state.polling = false;
    clearTimeout(state.timer);
    try {
      const response = await requestCollectionImport(username, {
        method: "DELETE",
        signal: state.controller.signal,
      });
      if (apply(response, state)) schedule(state);
    } catch (error) {
      if (session.current === state && error.name !== "AbortError") {
        setImportMessage(`Could not stop import: ${error.message}`);
        schedule(state);
      }
    } finally {
      state.mutating = false;
    }
  }, [username, importStatus, dialogs, apply, schedule]);
  const dismissImport = React.useCallback(async (jobId) => {
    const state = session.current;
    if (
      !state?.username ||
      state.job?.job_id !== jobId ||
      !isUnacknowledgedImport(state.job) ||
      state.pendingAcknowledgements.has(jobId)
    )
      return;
    state.pendingAcknowledgements.add(jobId);
    setDismissal({
      jobId,
      saving: true,
      error: "",
    });
    try {
      const job = await acknowledgeCollectionImport(state.username, jobId, {
        signal: state.controller.signal,
      });
      if (session.current !== state) return;
      state.acknowledgedJobs.set(jobId, job);
      // Acknowledging A may finish after the user has started job B.
      if (state.job?.job_id === jobId) {
        state.job = job;
        setImportResult(job);
        setImportMessage("");
        setDismissal(null);
      }
    } catch (error) {
      if (session.current !== state || error.name === "AbortError") return;
      if (state.job?.job_id === jobId) {
        setDismissal({
          jobId,
          saving: false,
          error: `Could not dismiss import: ${describeApiError(error)}`,
        });
        if ([404, 409].includes(error.status)) {
          state.needsStatus = true;
          state.version += 1;
          state.pollController?.abort();
          state.polling = false;
          await pollRef.current(state);
        }
      }
    } finally {
      state.pendingAcknowledgements.delete(jobId);
    }
  }, []);
  const resultVisible = isUnacknowledgedImport(importResult);
  const resultMessage = resultVisible
    ? importResult.status === "completed"
      ? "Discogs collection successfully imported"
      : `Import ${importResult.status}. Imported records remain available; queued enrichment may continue.`
    : "";
  const currentDismissal =
    resultVisible && dismissal?.jobId === importResult.job_id
      ? dismissal
      : null;
  return {
    imported,
    startingImport,
    importStatus,
    importMessage: resultMessage || importMessage,
    importNoticeStatus: resultVisible ? importResult.status : null,
    importNoticeJobId: resultVisible ? importResult.job_id : null,
    dismissingImport: currentDismissal?.saving || false,
    dismissalError: currentDismissal?.error || "",
    dismissImport,
    collectionRevision,
    startImport,
    cancelImport,
  };
}
